import { isRenderableLatLon } from '@/shared/geo/coordinates';
import { workerMobileProjectionSchema, workerSafetyEventSchema, type WorkerMobileProjection, type WorkerSafetyEvent } from '../worker-mobile/model';

export type FieldDataProvider = 'V2';

export interface VersionedRuntimeEntity {
  runtime_key: string;
  field_uid: string;
  version: number;
  [key: string]: unknown;
}

export interface NormalizedFieldState {
  fieldUid: string;
  provider: FieldDataProvider;
  routingVersion: number;
  ownershipEpoch: number;
  productsById: Record<string, VersionedRuntimeEntity>;
  groupsById: Record<string, VersionedRuntimeEntity>;
  markersByRuntimeKey: Record<string, VersionedRuntimeEntity>;
  alarmsByRuntimeKey: Record<string, VersionedRuntimeEntity>;
  mobileDevicesByRuntimeKey: Record<string, WorkerMobileProjection>;
  mobileDevicesReady: boolean;
  mobileSafetyEvents: readonly WorkerSafetyEvent[];
  snapshotAlarmVersionsByRuntimeKey: Record<string, number>;
  lastEventId: string | null;
  resetReason: string | null;
  accessRevoked: boolean;
}

export type NormalizedFieldAction =
  | {
      type: 'FIELD_RESET';
      fieldUid: string;
      provider: FieldDataProvider;
      routingVersion: number;
      ownershipEpoch: number;
      reason: string;
    }
  | {
      type: 'SNAPSHOT_RECEIVED';
      fieldUid: string;
      provider: FieldDataProvider;
      routingVersion: number;
      ownershipEpoch: number;
      products?: VersionedRuntimeEntity[];
      groups?: VersionedRuntimeEntity[];
      markers?: VersionedRuntimeEntity[];
      alarms?: VersionedRuntimeEntity[];
    }
  | {
      type: 'MOBILE_SNAPSHOT_RECEIVED';
      fieldUid: string;
      routingVersion: number;
      ownershipEpoch: number;
      mobileDevices: WorkerMobileProjection[];
    }
  | {
      type: 'MOBILE_DATA_UNAVAILABLE';
      fieldUid: string;
      routingVersion: number;
      ownershipEpoch: number;
      eventId?: string | null;
    }
  | {
      type: 'PATCH_RECEIVED';
      fieldUid: string;
      routingVersion: number;
      ownershipEpoch: number;
      eventId: string | null;
      entity: VersionedRuntimeEntity & { entity_type?: unknown };
    }
  | { type: 'FIELD_ACCESS_REVOKED'; fieldUid: string; reason: string };

export function createNormalizedFieldState(fieldUid = ''): NormalizedFieldState {
  return {
    fieldUid,
    provider: 'V2',
    routingVersion: 1,
    ownershipEpoch: 1,
    productsById: {},
    groupsById: {},
    markersByRuntimeKey: {},
    alarmsByRuntimeKey: {},
    mobileDevicesByRuntimeKey: {},
    mobileDevicesReady: false,
    mobileSafetyEvents: [],
    snapshotAlarmVersionsByRuntimeKey: {},
    lastEventId: null,
    resetReason: null,
    accessRevoked: false,
  };
}

function isPowerControlEntity(entity: VersionedRuntimeEntity): boolean {
  const modelCode = String(entity.model_code ?? entity.rx_profile_code ?? '')
    .trim()
    .toUpperCase();
  const componentType = String(entity.component_type ?? '')
    .trim()
    .toUpperCase();
  return modelCode === 'POWER_CONTROL_MODULE' || componentType === 'POWER_CONTROL';
}

function entityMap(items: readonly VersionedRuntimeEntity[], hidePowerControl = false): Record<string, VersionedRuntimeEntity> {
  const result: Record<string, VersionedRuntimeEntity> = {};
  for (const item of items) {
    if (!item.runtime_key || !Number.isSafeInteger(item.version) || item.version < 0 || (hidePowerControl && isPowerControlEntity(item))) {
      continue;
    }
    const current = result[item.runtime_key];
    if (!current || item.version > current.version) {
      result[item.runtime_key] = item;
    }
  }
  return result;
}

const CONFIGURATION_KEYS = [
  'name',
  'status',
  'model_name',
  'model_code',
  'product_model_uid',
  'instance_code',
  'install_info_uid',
  'install_location_text',
  'location',
  'display_config',
  'product_icon',
  'marker_visible',
  'is_escape_prevention_enabled',
  'configuration_version',
  'coordinate_source',
  'configured_position_at',
  'configured_latitude',
  'configured_longitude',
  'group',
  'components',
  'sensor_model_uid',
  'sensor_model_name',
  'rx_profile_code',
  'endpoint_id',
  'receiver_address',
  'sensor_address',
  'source_device_uid',
  'component_state',
];

function recipientMap(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/** 부모 수신 시각과 독립적인 연결 센서 상태는 원본 센서 버전으로 병합합니다. */
function mergeSourceRecipients(previous: VersionedRuntimeEntity, entity: VersionedRuntimeEntity, replaceMembership: boolean): unknown {
  const current = recipientMap(previous.source_recipients);
  const incoming = recipientMap(entity.source_recipients);
  if (!incoming || Number(entity.configuration_version ?? 0) < Number(previous.configuration_version ?? 0)) return previous.source_recipients;
  const merged: Record<string, unknown> = replaceMembership ? {} : { ...current };
  for (const [uid, raw] of Object.entries(incoming)) {
    const next = recipientMap(raw);
    const old = recipientMap(current?.[uid]);
    // 잘못된 버전이나 역순 수신으로 현재 경고를 숨기거나 해제된 경고를 되살리지 않습니다.
    if (!next || !Number.isSafeInteger(next.sensor_version) || Number(next.sensor_version) < 0) {
      if (old) merged[uid] = old;
      continue;
    }
    const oldKey = recipientMap(old?.warning_presentation_key);
    const nextKey = recipientMap(next.warning_presentation_key);
    const oldReceived = typeof oldKey?.last_received_at === 'string' ? Date.parse(oldKey.last_received_at) : NaN;
    const nextReceived = typeof nextKey?.last_received_at === 'string' ? Date.parse(nextKey.last_received_at) : NaN;
    const oldPresentationVersion =
      Number.isSafeInteger(old?.warning_presentation_version) && Number(old?.warning_presentation_version) >= 0 ? Number(old?.warning_presentation_version) : 0;
    // 알람 확인은 센서 수신 버전을 바꾸지 않으므로 같은 원본·표시 세대의 알람 버전을 함께 비교합니다.
    const presentationChanged =
      old &&
      next.sensor_version === old.sensor_version &&
      Number.isSafeInteger(next.warning_presentation_version) &&
      Number(next.warning_presentation_version) > oldPresentationVersion &&
      Number.isFinite(nextReceived) &&
      nextReceived === oldReceived &&
      typeof nextKey?.display_status === 'string' &&
      nextKey?.display_status === oldKey?.display_status;
    const retained = old && Number.isSafeInteger(old.sensor_version) && Number(next.sensor_version) <= Number(old.sensor_version) && !presentationChanged;
    if (retained && replaceMembership && next.sensor_version === old.sensor_version) {
      // 제품 구성이 바뀐 이름·지급 정보는 반영하되 같은 센서 버전의 경고 상태는 유지합니다.
      merged[uid] = {
        ...next,
        sensor_version: old.sensor_version,
        display_status: old.display_status,
        warning_reason: old.warning_reason,
        warning_presentation_key: old.warning_presentation_key,
        warning_presentation_version: old.warning_presentation_version,
      };
    } else {
      merged[uid] = retained ? old : next;
    }
  }
  if (current && Object.keys(merged).length === Object.keys(current).length && Object.entries(merged).every(([uid, value]) => current[uid] === value))
    return previous.source_recipients;
  return merged;
}

/** 구성 버전과 관측 버전을 각각 비교하여 지연된 한쪽 갱신이 다른 쪽을 되돌리지 않게 합니다. */
export function mergeRuntimeEntity(previous: VersionedRuntimeEntity, entity: VersionedRuntimeEntity): VersionedRuntimeEntity {
  const configChanged = Number(entity.configuration_version ?? 0) > Number(previous.configuration_version ?? 0);
  const sensorConfiguration = typeof entity.runtime_key === 'string' && entity.runtime_key.startsWith('sensor:') && Number.isSafeInteger(entity.configuration_version);
  const staleSensorState = sensorConfiguration && Number(entity.configuration_version) < Number(previous.configuration_version ?? 0);
  // 새 센서 구성의 미수신 상태(version 0)도 반영하며 이전 구성의 늦은 계측은 되살리지 않습니다.
  // 제품 이름 등 관리 정보와 관측의 독립 병합은 유지하고 실제 센서 구성에만 적용합니다.
  const stateChanged = !staleSensorState && (entity.version > previous.version || (sensorConfiguration && configChanged));
  const recipients = mergeSourceRecipients(previous, entity, stateChanged || configChanged);
  const recipientsChanged = recipients !== previous.source_recipients;
  if (!stateChanged && !configChanged && !recipientsChanged) return previous;
  const next = stateChanged ? { ...previous, ...entity } : { ...previous };
  if (recipientsChanged || 'source_recipients' in previous) next.source_recipients = recipients;
  const configuration = configChanged ? entity : previous;
  for (const key of CONFIGURATION_KEYS) {
    // 제품 상세의 내부 센서는 id만 제공할 수 있어 알람 키의 형식부터 확인합니다.
    // 알람의 OPEN/CLOSED는 구성값이 아니라 수신 버전으로 갱신하는 실행 상태입니다.
    if (key === 'status' && typeof entity.runtime_key === 'string' && entity.runtime_key.startsWith('alarm:')) continue;
    if (key in configuration) next[key] = configuration[key];
  }
  const inheritedPosition = entity.position_source === 'PARENT' && Number(entity.configuration_version ?? 0) >= Number(next.configuration_version ?? 0);
  if ('coordinate_source' in next) {
    // 표시 좌표와 관측 좌표를 분리하여 늦은 아이콘 저장이 최신 GPS를 되돌리지 않게 합니다.
    const configuredAt = typeof next.configured_position_at === 'string' ? Date.parse(next.configured_position_at) : NaN;
    const observedAt = typeof next.observed_position_at === 'string' ? Date.parse(next.observed_position_at) : NaN;
    const hasConfiguredPosition = isRenderableLatLon(next.configured_latitude, next.configured_longitude);
    const hasObservedPosition = isRenderableLatLon(next.observed_latitude, next.observed_longitude);
    // 시각이 있는 위치는 최신 좌표를 선택하고, 시각 없는 과거 응답은 기존 계약을 유지합니다.
    const fixed =
      hasConfiguredPosition &&
      (Number.isFinite(configuredAt) ? !hasObservedPosition || !Number.isFinite(observedAt) || configuredAt >= observedAt : next.coordinate_source === 'FIXED');
    // 미수신 관측 장비의 설정 좌표가 실시간 병합에서 다시 나타나는 것을 막습니다.
    const received = Boolean(next.last_received_at);
    const observed = received && hasObservedPosition;
    if (fixed || observed || (received && hasConfiguredPosition)) {
      next.latitude = fixed || !observed ? next.configured_latitude : next.observed_latitude;
      next.longitude = fixed || !observed ? next.configured_longitude : next.observed_longitude;
      next.coordinate_source = fixed ? 'FIXED' : 'OBSERVED';
      // 자체 GPS·지정 위치를 선택했으면 이전 부모 출처를 제거하여 지도에 즉시 나타나게 합니다.
      if (next.position_source === 'PARENT') next.position_source = observed && !fixed ? 'GPS' : null;
    } else if (inheritedPosition) {
      // 서버가 현재 부모 연결로 검증한 위치만 상속하며 자체 좌표와 관측 상태에는 복사하지 않습니다.
      const valid = isRenderableLatLon(entity.latitude, entity.longitude);
      next.latitude = valid ? entity.latitude : null;
      next.longitude = valid ? entity.longitude : null;
      next.position_source = 'PARENT';
    } else {
      next.latitude = null;
      next.longitude = null;
      if (next.position_source === 'PARENT') next.position_source = null;
    }
  } else if (inheritedPosition) {
    next.latitude = entity.latitude ?? null;
    next.longitude = entity.longitude ?? null;
    next.position_source = 'PARENT';
  }
  if (Array.isArray(entity.sensors) && Array.isArray(previous.sensors)) {
    const previousSensors = new Map(previous.sensors.map(sensor => [sensor.runtime_key, sensor]));
    const source = configChanged ? entity.sensors : previous.sensors;
    const incomingSensors = new Map(entity.sensors.map(sensor => [sensor.runtime_key, sensor]));
    next.sensors = source.map(sensor => {
      const current = previousSensors.get(sensor.runtime_key);
      const incoming = incomingSensors.get(sensor.runtime_key);
      return current && incoming ? mergeRuntimeEntity(current, incoming) : sensor;
    });
  }
  return next;
}

function mergeVersionedEntity(current: Record<string, VersionedRuntimeEntity>, entity: VersionedRuntimeEntity, hidePowerControl = false): Record<string, VersionedRuntimeEntity> {
  if (hidePowerControl && isPowerControlEntity(entity)) return current;
  const previous = current[entity.runtime_key];
  const merged = previous ? mergeRuntimeEntity(previous, entity) : entity;
  if (merged === previous) return current;
  return { ...current, [entity.runtime_key]: merged };
}

/**
 * field/provider/epoch 경계를 넘는 patch를 거부하고 entity별 단조 증가 version만 반영합니다.
 */
export function normalizedFieldReducer(state: NormalizedFieldState, action: NormalizedFieldAction): NormalizedFieldState {
  if (action.type === 'FIELD_ACCESS_REVOKED') {
    if (action.fieldUid !== state.fieldUid) return state;
    return { ...createNormalizedFieldState(action.fieldUid), provider: state.provider, resetReason: action.reason, accessRevoked: true };
  }

  if (action.type === 'FIELD_RESET') {
    // 저장된 옛 설정이나 외부 이벤트가 실행 공급자를 다시 열 수 없습니다.
    if (action.provider !== 'V2') return { ...createNormalizedFieldState(action.fieldUid), accessRevoked: true, resetReason: 'INVALID_PROVIDER' };
    return {
      ...createNormalizedFieldState(action.fieldUid),
      provider: action.provider,
      routingVersion: action.routingVersion,
      ownershipEpoch: action.ownershipEpoch,
      resetReason: action.reason,
    };
  }

  if (action.fieldUid !== state.fieldUid || action.routingVersion !== state.routingVersion || action.ownershipEpoch !== state.ownershipEpoch) {
    return state;
  }

  if (action.type === 'SNAPSHOT_RECEIVED') {
    if (action.provider !== state.provider) return state;
    const alarmsByRuntimeKey = entityMap(action.alarms ?? []);
    return {
      ...state,
      productsById: entityMap(action.products ?? []),
      groupsById: entityMap(action.groups ?? []),
      markersByRuntimeKey: entityMap(action.markers ?? [], true),
      alarmsByRuntimeKey,
      // 초기 이력과 이후 실시간 수신을 렌더 시점·브라우저 시계에 의존하지 않고 구분합니다.
      snapshotAlarmVersionsByRuntimeKey: Object.fromEntries(Object.values(alarmsByRuntimeKey).map(alarm => [alarm.runtime_key, alarm.version])),
      resetReason: null,
      accessRevoked: false,
    };
  }

  // 권한 취소 뒤 도착한 저장 응답과 실시간 patch가 차단된 현장 데이터를 되살리지 못하게 합니다.
  if (state.accessRevoked || state.provider !== 'V2') return state;
  if (action.type === 'MOBILE_DATA_UNAVAILABLE') {
    return { ...state, mobileDevicesReady: false, mobileDevicesByRuntimeKey: {}, lastEventId: action.eventId ?? state.lastEventId };
  }
  if (action.type === 'MOBILE_SNAPSHOT_RECEIVED') {
    const mobileDevicesByRuntimeKey: Record<string, WorkerMobileProjection> = Object.fromEntries(action.mobileDevices.map(device => [device.runtime_key, device]));
    // 조회 중 들어온 더 높은 버전의 SSE 관측·숨김을 이전 snapshot으로 되돌리지 않습니다.
    for (const device of Object.values(state.mobileDevicesByRuntimeKey)) {
      const snapshotDevice = mobileDevicesByRuntimeKey[device.runtime_key];
      if (!snapshotDevice || device.version > snapshotDevice.version) mobileDevicesByRuntimeKey[device.runtime_key] = device;
    }
    return { ...state, mobileDevicesReady: true, mobileDevicesByRuntimeKey };
  }
  const entityType = String(action.entity.entity_type ?? '').toUpperCase();
  if (action.entity.field_uid !== state.fieldUid) return state;
  const nextState = { ...state, lastEventId: action.eventId ?? state.lastEventId };
  if (entityType === 'MOBILE_SAFETY_EVENT') {
    const parsed = workerSafetyEventSchema.safeParse(action.entity);
    if (!parsed.success || state.mobileSafetyEvents.some(event => event.event_id === parsed.data.event_id)) return nextState;
    return { ...nextState, mobileSafetyEvents: [...state.mobileSafetyEvents, parsed.data].slice(-100) };
  }
  if (entityType === 'MOBILE_DEVICE') {
    const parsed = workerMobileProjectionSchema.safeParse(action.entity);
    if (!parsed.success) return state;
    const previous = state.mobileDevicesByRuntimeKey[parsed.data.runtime_key];
    if (previous && previous.version >= parsed.data.version) return nextState;
    return { ...nextState, mobileDevicesByRuntimeKey: { ...state.mobileDevicesByRuntimeKey, [parsed.data.runtime_key]: parsed.data } };
  }
  if (entityType === 'ALARM') {
    return { ...nextState, alarmsByRuntimeKey: mergeVersionedEntity(state.alarmsByRuntimeKey, action.entity) };
  }
  if (entityType === 'GROUP') {
    return { ...nextState, groupsById: mergeVersionedEntity(state.groupsById, action.entity) };
  }
  if (entityType === 'PRODUCT') {
    return {
      ...nextState,
      productsById: mergeVersionedEntity(state.productsById, action.entity),
      markersByRuntimeKey: mergeVersionedEntity(state.markersByRuntimeKey, action.entity, true),
    };
  }
  return state;
}

/** 과거 운영 단계나 조회 실패를 이전 데이터 공급자로 우회하지 않습니다. */
export function selectFieldDataProvider(activePhase: unknown): FieldDataProvider {
  if (activePhase !== 'V2_ACTIVE') throw new Error('현장의 장비 운영 준비가 완료되지 않았습니다.');
  return 'V2';
}
